import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import {
  ArrowLeft,
  ArrowRight,
  Send,
  Loader2,
  ShieldCheck,
} from "lucide-react";
import Markdown from "react-markdown";
import toast from "react-hot-toast";
import { apiService } from "../services/api";
import { useAuth } from "../hooks/useAuth";
import { canManageAssessment } from "../types";
import type { DataActAnswer, DataActQuestionsJSON } from "../types";
import {
  getVisibleQuestions,
  pruneUnreachableAnswers,
} from "../utils/dataActConditions";
import { useAssessmentMutations } from "../hooks/useAssessmentMutations";
import AssessmentPageShell from "../components/assessment/AssessmentPageShell";
import DataActAssessmentQuestion from "../components/DataActAssessmentQuestion";

// The CRA applicability assessment is a single-path branching questionnaire, so
// answers are a flat questionId -> answer map (no product/service path and no
// per-question "details" field like the Data Act questionnaire has).
type AnswerMap = Record<string, string>;

const HW_QUESTION = "hardware-or-software-or-combo";
const NONE_DESCRIPTION = "none-of-the-above-description";

const CRA_INSTRUCTIONS_LINK =
  "https://intranet.sandvik.com/thewaywework/policy--procedure/it-security-policy/Documents/3.4.8.1.8.1%20Cyber%20Resilience%20Act%20Instruction.pdf";

const CLASSIFICATION_LABELS: Record<string, string> = {
  cra_critical: "Critical",
  cra_important_2: "Important Class II",
  cra_important_1: "Important Class I",
  cra_default: "Default",
};

function getCRAResultMessage(result: string, reason?: string): string {
  if (result === "not_in_scope") {
    const reasonText = reason ? ` - ${reason}` : "";
    return `Out of CRA scope${reasonText}.<br>Seek consultation if further clarity is needed`;
  }
  if (result === "cra_partially") {
    return `CRA is partially applicable. The CRA reporting obligations apply, but CRA requirements regarding product properties do not apply. See <a href='${CRA_INSTRUCTIONS_LINK}' target="_blank" rel="noopener noreferrer">this link</a> for further information about the CRA reporting obligations.`;
  }
  if (result === "cra_determined_by_product") {
    return `Please note that even though internally developed or manufactured component, subsystem or other resource would not be placed on the market as a separate individual product, <b>it may still face certain CRA requirements</b> as needed for the Sandvik end-product to be CRA compliant. Please seek confirmation on whether or not the end-product to which the subsystem, component or resource is part of is under the scope of CRA.`;
  }
  if (CLASSIFICATION_LABELS[result]) {
    return `The CRA applies to the product. The CRA classification is ${CLASSIFICATION_LABELS[result]}. See further instructions <a href='${CRA_INSTRUCTIONS_LINK}' target="_blank" rel="noopener noreferrer">here</a>.`;
  }
  return `Assessment complete. Result: ${result}`;
}

function answersToList(map: AnswerMap): DataActAnswer[] {
  return Object.entries(map)
    .filter(([, answer]) => answer !== "")
    .map(([questionId, answer]) => ({ questionId, answer, details: "" }));
}

export default function CRAAssessmentPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const hasRedirected = useRef(false);

  const [answers, setAnswers] = useState<AnswerMap>({});
  const [started, setStarted] = useState(false);
  const [resultView, setResultView] = useState<{
    result: string;
    resultReason?: string;
  } | null>(null);
  const prefilledRef = useRef(false);

  // ── Data loading ──

  const { data: productData, isLoading: productLoading } = useQuery(
    ["product", id],
    () => apiService.getProduct(id!),
    { enabled: !!id },
  );
  const product = productData?.data?.data;

  const {
    data: questionsData,
    isLoading: questionsLoading,
    error: questionsError,
  } = useQuery("craQuestionsV2", apiService.getCRAQuestionsV2, {
    retry: 2,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000,
  });

  const schema = questionsData?.data?.data as DataActQuestionsJSON | undefined;
  const section = schema?.sections?.[0] ?? null;

  // The latest assessment is used purely to prefill previously stored answers
  // so an existing assessment can be reviewed and edited. A missing assessment
  // is a normal "not started yet" state, so any error is swallowed.
  const { data: latestData, isLoading: latestLoading } = useQuery(
    ["craLatest", id],
    () => apiService.getLatestCRAAssessment(id!),
    { enabled: !!id, retry: false },
  );

  // ── Ownership guard ──

  useEffect(() => {
    if (
      !productLoading &&
      product &&
      user &&
      !canManageAssessment(user, product.ownerId) &&
      !hasRedirected.current
    ) {
      hasRedirected.current = true;
      toast.error(
        "Only the product owner or an admin can create or edit assessments",
      );
      navigate("/dashboard");
    }
  }, [product, user, navigate, productLoading]);

  // ── Prefill from the latest stored assessment ──

  useEffect(() => {
    // Wait until the latest-assessment query has settled before deciding
    // whether there are stored answers. Latching the ref while `latestData` is
    // still loading would permanently skip the prefill once the data arrives.
    if (prefilledRef.current || !section || latestLoading) return;
    const raw = latestData?.data?.data?.answers;
    if (!raw) {
      prefilledRef.current = true;
      return;
    }
    try {
      const stored: { questionId: string; answer: string; reason?: string }[] =
        typeof raw === "string" ? JSON.parse(raw) : raw;
      const map: AnswerMap = {};
      for (const a of stored) {
        map[a.questionId] = String(a.answer);
      }
      // The free-text "none of the above" explanation is persisted as the
      // `reason` of the hardware/software question (mirroring how it is sent on
      // submit), so lift it back into its own field for editing.
      const hw = stored.find((a) => a.questionId === HW_QUESTION);
      if (hw?.reason && hw.answer === "none-of-the-above") {
        map[NONE_DESCRIPTION] = hw.reason;
      }
      const pruned = pruneUnreachableAnswers([section], answersToList(map));
      const next: AnswerMap = {};
      for (const a of pruned) next[a.questionId] = a.answer;
      setAnswers(next);
      // An existing assessment is being reviewed/edited, so skip the intro gate.
      if (Object.keys(next).length > 0) setStarted(true);
    } catch {
      // Corrupt stored answers should not block starting a fresh assessment.
    }
    prefilledRef.current = true;
  }, [latestData, latestLoading, section]);

  // ── Derived state ──

  const answersList = useMemo(() => answersToList(answers), [answers]);

  const visibleQuestions = useMemo(
    () => (section ? getVisibleQuestions(section, answersList) : []),
    [section, answersList],
  );

  const canEdit = canManageAssessment(user, product?.ownerId) && !resultView;

  const handleChange = (
    questionId: string,
    _field: "answer" | "details",
    value: string,
  ) => {
    setAnswers((prev) => {
      const merged: AnswerMap = { ...prev, [questionId]: value };
      if (!section) return merged;
      // Collapse the downstream chain when an earlier branch changes so stale
      // answers from a no-longer-reachable path are not submitted.
      const pruned = pruneUnreachableAnswers([section], answersToList(merged));
      const next: AnswerMap = {};
      for (const a of pruned) next[a.questionId] = a.answer;
      return next;
    });
  };

  // ── Submit ──

  const buildPayload = () => {
    const list = answersList
      .filter((a) => a.questionId !== NONE_DESCRIPTION)
      .map((a) => {
        const reason =
          a.questionId === HW_QUESTION &&
          a.answer === "none-of-the-above" &&
          answers[NONE_DESCRIPTION]?.trim()
            ? answers[NONE_DESCRIPTION].trim()
            : undefined;
        return reason
          ? { questionId: a.questionId, answer: a.answer, reason }
          : { questionId: a.questionId, answer: a.answer };
      });
    return { answers: list, version: schema?.version || "1.0" };
  };

  const { submitMutation, isSubmitting } = useAssessmentMutations(
    {
      saveFn: (payload: any) => apiService.createCRAAssessment(id!, payload),
      // The CRA assessment is computed in a single step: the payload is rebuilt
      // from the current answers here, so the assessment-id argument (unused) is
      // irrelevant.
      submitFn: () => apiService.createCRAAssessment(id!, buildPayload()),
      invalidateKeys: [
        ["product", id!],
        ["craLatest", id!],
        ["assessmentStatus", id!],
      ],
      entityLabel: "Assessment",
    },
    {
      onSubmitSuccess: (response: any) => {
        const data = response?.data?.data;
        if (data?.result) {
          setResultView({
            result: data.result,
            resultReason: data.resultReason,
          });
        }
      },
    },
  );

  const handleSubmit = () => {
    if (!canEdit || !section) return;
    const missing = visibleQuestions
      .filter((q) => q.required && !q.infoOnly && !answers[q.id]?.trim())
      .map((q) => q.id);
    if (missing.length > 0) {
      toast.error("Please answer all questions before submitting.");
      return;
    }
    submitMutation.mutate(id!);
  };

  // ── Render ──

  const isLoading = productLoading || questionsLoading || latestLoading;
  const error =
    questionsError || (!isLoading && !section)
      ? "Failed to load assessment questions"
      : null;

  if (resultView) {
    return (
      <AssessmentPageShell
        title="CRA Applicability Assessment — Result"
        subtitle={product?.name}
        backTo={`/products/${id}`}
        showActions={false}
      >
        <div className="max-w-3xl mx-auto py-10 px-6">
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Assessment Complete
            </h3>
            <p
              className="text-base text-gray-700 dark:text-gray-300 leading-relaxed [&_a]:text-blue-600 [&_a]:underline"
              // Result copy is built from a fixed set of trusted strings plus
              // the backend-computed reason; it contains simple inline markup.
              dangerouslySetInnerHTML={{
                __html: getCRAResultMessage(
                  resultView.result,
                  resultView.resultReason,
                ),
              }}
            />
          </div>
          <div className="mt-8 flex justify-center">
            <button
              onClick={() => navigate(`/products/${id}`)}
              className="btn btn-primary"
            >
              Back to Product
            </button>
          </div>
        </div>
      </AssessmentPageShell>
    );
  }

  return (
    <AssessmentPageShell
      title="CRA Applicability Assessment"
      subtitle={product?.name}
      backTo={`/products/${id}`}
      isLoading={isLoading}
      error={error}
      showActions={false}
    >
      <div className="max-w-3xl mx-auto py-6 px-6">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Complete the Cyber Resilience Act assessment for this product.
          Additional questions appear as you answer.
        </p>

        {section?.description && (
          <div className="relative overflow-hidden rounded-2xl border border-teal-100 dark:border-teal-800/40 bg-gradient-to-br from-teal-50 via-white to-blue-50 dark:from-teal-900/20 dark:via-gray-900 dark:to-blue-900/20 p-8 mb-6 shadow-sm">
            <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-teal-200/30 dark:bg-teal-500/10 blur-2xl" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-teal-600 text-white shadow-sm">
                  <ShieldCheck className="h-6 w-6" />
                </span>
                <h2 className="text-2xl font-bold text-teal-700 dark:text-teal-300">
                  CRA Applicability Assessment
                </h2>
              </div>
              <Markdown
                components={{
                  p: ({ children }) => (
                    <p className="mb-0 text-base font-medium leading-relaxed text-gray-700 dark:text-gray-200">
                      {children}
                    </p>
                  ),
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-teal-600 dark:text-teal-400 underline decoration-teal-300 underline-offset-2 hover:text-teal-700 dark:hover:text-teal-300"
                    >
                      {children}
                    </a>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-bold text-gray-900 dark:text-white">
                      {children}
                    </strong>
                  ),
                }}
              >
                {section.description}
              </Markdown>

              {!started && (
                <button
                  onClick={() => setStarted(true)}
                  className="mt-6 inline-flex items-center gap-2 rounded-lg bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
                >
                  Start the Survey
                  <ArrowRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        )}

        <div className={started ? "space-y-8" : "hidden"}>
          {visibleQuestions.map((question) => {
            const isAnswered = !!answers[question.id];
            const railClass = isAnswered
              ? "border-l-4 border-green-400 dark:border-green-600"
              : question.required
                ? "border-l-4 border-amber-400 dark:border-amber-500"
                : "border-l-4 border-gray-200 dark:border-gray-700";
            return (
              <div
                key={question.id}
                data-question-id={question.id}
                className={`pl-4 pb-8 border-b border-gray-100 dark:border-gray-800 last:border-b-0 ${railClass}`}
              >
                <DataActAssessmentQuestion
                  question={question}
                  answer={answers[question.id] ?? ""}
                  onChange={handleChange}
                  disabled={!canEdit}
                  pathKey="cra"
                />
              </div>
            );
          })}
        </div>

        {/* Documentation link */}
        <div
          className={`bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-200 dark:border-blue-800/30 p-4 mt-6 ${started ? "" : "hidden"}`}
        >
          <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">
            Need more guidance?
          </h3>
          <p className="mt-1 text-sm text-blue-700 dark:text-blue-400">
            For definitions see:
            <a
              href="https://sandvik.sharepoint.com/teams/isms/SitePages/Projects---CRA-and-SDL-Project-Questions-And-Answers.aspx#cra-vocabulary"
              target="_blank"
              rel="noopener noreferrer"
              className="ml-1 font-medium text-blue-800 dark:text-blue-300 underline hover:text-blue-900 dark:hover:text-blue-200"
            >
              Vocabulary
            </a>
          </p>
        </div>

        <div
          className={`flex items-center justify-between pt-4 ${started ? "" : "hidden"}`}
        >
          <button
            onClick={() => navigate(`/products/${id}`)}
            className="btn btn-outline"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Product
          </button>
          {canEdit && (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-teal-700 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Submit
            </button>
          )}
        </div>
      </div>
    </AssessmentPageShell>
  );
}
